import React, { useCallback, useMemo, useState } from "react";
import {
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { Dropdown, DropdownItem } from "../../BaseUI/Dropdown";
import {
  canNavigatePeriodForward,
  createPeriodState,
  periodNavLabel,
  periodRangeSummary,
  shiftPeriodAnchor,
  todayPeriodStr,
} from "../../../utils/periodDateUtils";
import styles from "./PeriodDateSelector.module.scss";

const PRESETS = [
  { value: "day", label: "Bugün" },
  { value: "week", label: "Bu hafta" },
  { value: "month", label: "Bu ay" },
];

const PeriodDateSelector = ({
  mode = "day",
  anchorDate,
  startDate: startDateProp,
  endDate: endDateProp,
  onChange,
  maxDate = todayPeriodStr(),
  className = "",
  dayOnly = false,
}) => {
  const today = maxDate || todayPeriodStr();
  const resolvedMode = dayOnly ? "day" : mode;
  const [customDraft, setCustomDraft] = useState({ start: "", end: "" });

  const period = useMemo(
    () =>
      createPeriodState(resolvedMode, anchorDate || today, {
        startDate: startDateProp,
        endDate: endDateProp,
      }),
    [resolvedMode, anchorDate, startDateProp, endDateProp, today],
  );

  const { startDate, endDate } = period;

  const triggerLabel = useMemo(
    () => periodNavLabel(resolvedMode, period.anchorDate, startDate, endDate),
    [resolvedMode, period.anchorDate, startDate, endDate],
  );

  const rangeSummary = useMemo(
    () => periodRangeSummary(startDate, endDate),
    [startDate, endDate],
  );

  const canGoForward = canNavigatePeriodForward(
    resolvedMode,
    period.anchorDate,
    today,
    { startDate, endDate },
  );
  const canGoBack = resolvedMode !== "custom";
  const showJumpToday = !(resolvedMode === "day" && startDate === today);

  const emit = useCallback(
    (next) => onChange?.(next),
    [onChange],
  );

  const setPreset = (nextMode) => {
    emit(createPeriodState(nextMode, today));
  };

  const shift = (direction) => {
    if (resolvedMode === "custom") return;
    emit(
      createPeriodState(
        resolvedMode,
        shiftPeriodAnchor(resolvedMode, period.anchorDate, direction),
      ),
    );
  };

  const goToday = () => emit(createPeriodState("day", today));

  const openCustom = () => {
    setCustomDraft({ start: startDate, end: endDate });
    emit(createPeriodState("custom", startDate, { startDate, endDate }));
  };

  const applyCustom = () => {
    if (!customDraft.start || !customDraft.end) return;
    emit(
      createPeriodState("custom", customDraft.start, {
        startDate: customDraft.start,
        endDate: customDraft.end,
      }),
    );
  };

  const rootClass = `${styles.root} ${dayOnly ? styles.dayOnly : ""} ${className}`.trim();

  return (
    <div className={rootClass} role="group" aria-label="Tarih dönemi">
      <button
        type="button"
        className={styles.arrowBtn}
        onClick={() => shift(-1)}
        disabled={!canGoBack}
        aria-label="Önceki dönem"
      >
        <FiChevronLeft size={16} />
      </button>

      <span className={styles.divider} aria-hidden />

      {dayOnly ? (
        <button
          type="button"
          className={styles.trigger}
          onClick={showJumpToday ? goToday : undefined}
          title={showJumpToday ? "Bugün'e git" : undefined}
        >
          <span className={styles.triggerLabel} title={triggerLabel}>
            {triggerLabel}
          </span>
        </button>
      ) : (
        <Dropdown
          className={styles.dropdown}
          customTrigger
          hideDropdownIcon
          closeOnSelect={false}
          placement="bottom-end"
          border="none"
          maxHeight="none"
          contentClassName={styles.popoverShell}
          trigger={
            <button type="button" className={styles.trigger} aria-haspopup="listbox">
              <FiCalendar size={14} className={styles.triggerIcon} aria-hidden />
              <span className={styles.triggerLabel} title={triggerLabel}>
                {triggerLabel}
              </span>
              <FiChevronDown size={13} className={styles.triggerCaret} aria-hidden />
            </button>
          }
        >
        <div className={styles.panel}>
          <p className={styles.heading}>Dönem</p>
          <ul className={styles.presetList} role="listbox" aria-label="Dönem seç">
            {PRESETS.map((preset) => {
              const active = mode === preset.value;
              return (
                <li key={preset.value}>
                  <DropdownItem
                    onClick={() => setPreset(preset.value)}
                    closeOnSelect
                  >
                    <span
                      className={`${styles.presetItem} ${active ? styles.presetItemActive : ""}`}
                      role="option"
                      aria-selected={active}
                    >
                      <span>{preset.label}</span>
                      {active && <FiCheck size={14} className={styles.check} />}
                    </span>
                  </DropdownItem>
                </li>
              );
            })}
          </ul>

          <div className={styles.rule} />

          <button
            type="button"
            className={`${styles.presetItem} ${mode === "custom" ? styles.presetItemActive : ""}`}
            onClick={openCustom}
          >
            <span>Özel aralık</span>
            {mode === "custom" && <FiCheck size={14} className={styles.check} />}
          </button>

          {mode === "custom" && (
            <div className={styles.customFields}>
              <label className={styles.field}>
                <span>Başlangıç</span>
                <input
                  type="date"
                  value={customDraft.start || startDate}
                  max={customDraft.end || today}
                  onChange={(e) =>
                    setCustomDraft((prev) => ({ ...prev, start: e.target.value }))
                  }
                />
              </label>
              <label className={styles.field}>
                <span>Bitiş</span>
                <input
                  type="date"
                  value={customDraft.end || endDate}
                  min={customDraft.start || undefined}
                  max={today}
                  onChange={(e) =>
                    setCustomDraft((prev) => ({ ...prev, end: e.target.value }))
                  }
                />
              </label>
              <button
                type="button"
                className={styles.applyBtn}
                onClick={applyCustom}
                disabled={!customDraft.start || !customDraft.end}
              >
                Uygula
              </button>
            </div>
          )}

          <p className={styles.summary}>{rangeSummary}</p>

          {showJumpToday && (
            <button type="button" className={styles.todayBtn} onClick={goToday}>
              Bugün&apos;e git
            </button>
          )}
        </div>
      </Dropdown>
      )}

      <span className={styles.divider} aria-hidden />

      <button
        type="button"
        className={styles.arrowBtn}
        onClick={() => shift(1)}
        disabled={!canGoForward}
        aria-label="Sonraki dönem"
      >
        <FiChevronRight size={16} />
      </button>
    </div>
  );
};

export default PeriodDateSelector;
